<template>
  <ComboboxRoot
    v-model="selectedOption"
    v-model:open="isOpen"
    ignore-filter
    class="contents"
  >
    <ComboboxAnchor
      class="relative flex w-full items-stretch overflow-hidden rounded-lg border focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500"
      :class="[
        (!state || state.status === 'pending') && `design-system-input`,
        state?.status === 'success' &&
          `border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-900/50`,
        state?.status === 'error' &&
          `border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-900/50`,
      ]"
    >
      <RadixComboboxInput
        :id="id ?? settingId"
        v-model="query"
        :display-value="renderDisplayValue"
        :placeholder="placeholder"
        class="min-w-0 grow border-0 bg-transparent px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:ring-0"
        @focus="handleFocus"
      />

      <div
        v-if="isPending || state?.status === 'pending'"
        class="-my-1 -mr-2.5 p-2.5"
      >
        <ArrowPathIcon
          class="m-0.5 size-4 animate-spin text-muted-foreground"
        />
      </div>

      <div
        v-if="state && state.status !== 'pending'"
        class="-my-1 -mr-2.5 p-2.5"
      >
        <template v-if="state.status === 'success'">
          <CheckCircleIcon class="size-5 text-green-500" />
        </template>
        <template v-else-if="state.status === 'error'">
          <Tooltip.Root interactive>
            <Tooltip.Trigger>
              <ExclamationCircleIcon class="size-5 text-red-500" />
            </Tooltip.Trigger>

            <Tooltip.Content>
              {{ state.message }}

              <RouterLink
                v-if="state.route"
                :to="state.route"
                class="mt-2 block cursor-pointer rounded bg-blue-500 px-2 py-1 text-xs font-bold uppercase text-white"
              >
                Click here
              </RouterLink>
            </Tooltip.Content>
          </Tooltip.Root>
        </template>
      </div>

      <ComboboxTrigger class="px-2.5 py-1.5 data-[state=open]:rotate-180">
        <ChevronDownIcon class="size-5 text-muted-foreground" />
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxPortal>
      <Transition
        enter-active-class="transition-opacity duration-100 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <ComboboxContent
          position="popper"
          :collision-padding="10"
          class="z-20 flex w-full flex-col rounded-md bg-gray-900 text-white shadow-lg dark:border dark:border-border dark:bg-card"
          :class="$style.combobox_content"
        >
          <ComboboxViewport class="py-2">
            <slot v-if="filteredOptions.length === 0" name="empty-state">
              <div class="flex h-20 flex-col items-center justify-center px-3">
                <p class="text-sm text-muted-foreground">No items</p>
              </div>
            </slot>

            <ComboboxVirtualizer
              v-else-if="virtualized"
              :options="mutableFilteredOptions"
              :estimate-size="32"
              :text-content="getOptionTextContent"
            >
              <template #default="{ option }">
                <ComboboxItem
                  :value="option"
                  :disabled="option.disabled"
                  class="group relative mx-2 flex shrink-0 cursor-pointer select-none scroll-m-2 gap-3 self-stretch rounded-md border-none px-3 py-1 text-left text-sm outline-none transition-all duration-75 data-[highlighted]:bg-gray-700 dark:data-[highlighted]:bg-muted"
                >
                  <span
                    class="min-w-0 grow break-words transition-opacity group-data-[disabled]:opacity-50"
                  >
                    {{ option.label }}
                  </span>

                  <CheckIcon
                    v-if="option.id !== '' || !option.disabled"
                    class="my-0.5 size-4 shrink-0 text-blue-400 opacity-0 transition-opacity duration-75 group-data-[state=checked]:opacity-100"
                  />
                </ComboboxItem>
              </template>
            </ComboboxVirtualizer>

            <template v-else>
              <template
                v-for="(children, groupName, idx) in groupedOptions"
                :key="groupName"
              >
                <ComboboxSeparator
                  v-if="idx !== 0"
                  class="my-2 border-t border-gray-700 dark:border-border"
                />

                <ComboboxGroup>
                  <ComboboxLabel
                    v-if="groupName"
                    class="mx-5 my-1 shrink-0 select-none border-t-border text-[0.8125rem] leading-5 text-muted-foreground"
                  >
                    {{ groupName }}
                  </ComboboxLabel>

                  <ComboboxItem
                    v-for="option in children"
                    :key="option.id"
                    :value="option"
                    :disabled="option.disabled"
                    class="group relative mx-2 flex shrink-0 cursor-pointer select-none scroll-m-2 gap-3 self-stretch rounded-md border-none px-3 py-1 text-left text-sm outline-none transition-all duration-75 data-[highlighted]:bg-gray-700 dark:data-[highlighted]:bg-muted"
                  >
                    <span
                      class="min-w-0 grow break-words transition-opacity group-data-[disabled]:opacity-50"
                    >
                      {{ option.label }}
                    </span>

                    <CheckIcon
                      v-if="option.id !== '' || !option.disabled"
                      class="my-0.5 size-4 shrink-0 text-blue-400 opacity-0 transition-opacity duration-75 group-data-[state=checked]:opacity-100"
                    />
                  </ComboboxItem>
                </ComboboxGroup>
              </template>
            </template>
          </ComboboxViewport>
        </ComboboxContent>
      </Transition>
    </ComboboxPortal>
  </ComboboxRoot>
</template>

<script setup lang="ts">
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxGroup,
  ComboboxInput as RadixComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxViewport,
  ComboboxVirtualizer,
} from "reka-ui";
import { computed, ref, toRef } from "vue";
import { RouterLink } from "vue-router/auto";

import { useSettingControl } from "@/components/Setting/context";
import Tooltip from "@/design_system/Tooltip";
import ArrowPathIcon from "@/icons/heroicons/arrow-path-micro.svg";
import CheckCircleIcon from "@/icons/heroicons/check-circle-mini.svg";
import CheckIcon from "@/icons/heroicons/check-micro.svg";
import ChevronDownIcon from "@/icons/heroicons/chevron-down-micro.svg";
import ExclamationCircleIcon from "@/icons/heroicons/exclamation-circle-mini.svg";
import { useStablePending } from "@/lib/composables/stable-pending";

import type { ValidationStatus } from "./types";

export interface ComboboxOption {
  id: string;
  label: string;
  disabled?: boolean;
  group?: string;
}

const modelValue = defineModel<string>({ default: "" });
const query = defineModel<string>("query", { default: "" });

const props = defineProps<{
  options: readonly ComboboxOption[];
  placeholder?: string;
  pending?: boolean;
  state?: ValidationStatus | null;
  unfiltered?: boolean;
  id?: string;
  openOnFocus?: boolean;
  virtualized?: boolean;
}>();

const isOpen = ref(false);

const handleFocus = () => {
  if (props.openOnFocus) {
    isOpen.value = true;
  }
};

const isPending = useStablePending(toRef(props, "pending"));

const selectedOption = computed({
  get() {
    const value = modelValue.value;
    const option = props.options.find((opt) => opt.id === value);

    return option;
  },
  set(next) {
    modelValue.value = next ? next.id : "";
  },
});

const filteredOptions = computed(() => {
  if (props.unfiltered) {
    return [...props.options];
  }

  const q = query.value.trim().toLowerCase();

  return props.options.filter((option) => {
    return option.label.toLowerCase().includes(q);
  });
});

const mutableFilteredOptions = computed(() => [...filteredOptions.value]);

const getOptionTextContent = (opt: ComboboxOption) => opt.label;

const groupedOptions = computed(() => {
  const ungroupedOptions: ComboboxOption[] = [];
  const groups: Record<string, ComboboxOption[]> = {};

  for (const option of filteredOptions.value) {
    const groupName = option.group ?? "";

    if (groupName === "") {
      ungroupedOptions.push(option);
      continue;
    }

    const group = (groups[groupName] ??= []);
    group.push(option);
  }

  // Always sort ungrouped options first
  return ungroupedOptions.length ? { "": ungroupedOptions, ...groups } : groups;
});

const renderDisplayValue = (item: unknown): string => {
  if (!item) return "";
  return (item as ComboboxOption).label;
};

const setting = useSettingControl();
const settingId = setting?.register();
</script>

<style module>
.combobox_content {
  max-height: var(--reka-combobox-content-available-height);
  width: var(--reka-combobox-trigger-width);
}
</style>
